import type { EditorView } from "prosemirror-view";
import { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { useEditor, EditorContent } from "@tiptap/react";
import type { Editor } from "@tiptap/core";
import Document from "@tiptap/extension-document";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import History from "@tiptap/extension-history";
import Placeholder from "@tiptap/extension-placeholder";

import { TokenSuggestion } from "./extensions/typingSuggestions/TokenSuggestion";
import { suggestionPluginKey } from "./extensions/typingSuggestions/token/plugin";
import { PUCK_CONFIG } from "../constants";

const singleLineEditorSx = (readOnly?: boolean) => ({
    background: readOnly ? "var(--puck-color-grey-11)" : "var(--puck-color-white)",
    borderWidth: "1px",
    borderStyle: "solid",
    borderColor: "var(--puck-color-grey-09)",
    borderRadius: "4px",
    boxSizing: "border-box",
    fontFamily: "inherit",
    fontSize: "14px",
    width: "100%",
    maxWidth: "100%",
    transition: "border-color 50ms ease-in",
    cursor: readOnly ? "default" : "text",
    "&:hover":
        readOnly ?
            {}
        :   {
                borderColor: "var(--puck-color-grey-05)",
                transition: "none",
            },
    "&:focus-within":
        readOnly ?
            {}
        :   {
                borderColor: "var(--puck-color-grey-05)",
                outline: "2px solid var(--puck-color-azure-05)",
                transition: "none",
            },
    "& .ProseMirror": {
        minHeight: "unset",
        maxHeight: "unset",
        padding: "12px 15px",
        outline: "none",
        fontFamily: PUCK_CONFIG.FontFamily,
        fontSize: "14px",
        lineHeight: 1.5,
        whiteSpace: "nowrap",
        overflowX: "auto",
        color: readOnly ? "var(--puck-color-grey-04)" : "inherit",
        "& p": {
            margin: 0,
        },
        "&:focus": {
            outline: "none",
        },
        "& .ProseMirror-placeholder": {
            color: "var(--puck-color-grey-05)",
            pointerEvents: "none",
            position: "absolute",
            top: "12px",
            left: "15px",
        },
    },
});

export type TokenizedTextFieldProps = {
    onChange: (value: string) => void;
    value?: string;
    placeholder?: string;
    readOnly?: boolean;
};

export const TokenizedTextField = (props: TokenizedTextFieldProps) => {
    const { onChange, value = "", placeholder, readOnly } = props;
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const lastUserInputRef = useRef<number>(0);
    const [isEditorFocused, setIsEditorFocused] = useState(false);

    const extractPlainText = (html: string): string => {
        if (!html) return "";
        const temp = document.createElement("div");
        temp.innerHTML = html;
        return temp.textContent || temp.innerText || "";
    };

    const debouncedOnChange = (html: string) => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        lastUserInputRef.current = Date.now();
        debounceTimerRef.current = setTimeout(() => {
            onChange(extractPlainText(html));
        }, 300);
    };

    const editor = useEditor({
        editable: !readOnly,
        extensions: [
            Document,
            Paragraph,
            Text,
            History,
            Placeholder.configure({
                placeholder: placeholder || "",
            }),
            TokenSuggestion,
        ],
        content: value ? `<p>${value}</p>` : "",
        editorProps: {
            attributes: {
                class: "focus-visible",
            },
            handleKeyDown: (view: EditorView, event: KeyboardEvent) => {
                if (event.key === "Enter") {
                    const suggestionState = suggestionPluginKey.getState(view.state);
                    if (suggestionState?.active && suggestionState.suggestions.length > 0) {
                        return false;
                    }

                    event.preventDefault();
                    return true;
                }
                return false;
            },
        },
        onUpdate: ({ editor: ed }: { editor: Editor }) => {
            lastUserInputRef.current = Date.now();
            const html = ed.getHTML();
            debouncedOnChange(html);
        },
        onFocus: () => {
            setIsEditorFocused(true);
        },
        onBlur: ({ editor: ed }: { editor: Editor }) => {
            setIsEditorFocused(false);
            const html = ed.getHTML();
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
            onChange(extractPlainText(html));
        },
    });

    useEffect(() => {
        if (!editor) return;

        const currentText = extractPlainText(editor.getHTML());
        const timeSinceLastInput = Date.now() - lastUserInputRef.current;

        if (currentText !== value && (!isEditorFocused || timeSinceLastInput > 500)) {
            editor.commands.setContent(value ? `<p>${value}</p>` : "");
        }
    }, [value, editor, isEditorFocused]);

    return (
        <Box sx={singleLineEditorSx(readOnly)}>
            <EditorContent editor={editor} />
        </Box>
    );
};

export default TokenizedTextField;
